import {
  CreateGroupRequest,
  DeleteGroupRequest,
  GetGroupRequest,
  GroupsRequest,
  UpdateGroupRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination } from '~/common/types';
import { Group, GroupRequest } from '../types';
import { Form } from '~/forms/types';
import { User } from '~/users/types';
import { DocumentID } from '~/documents/types';
import { Training } from '~/training/types';
import { Feature } from '~/features/types';

export class GroupService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async fetch(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GroupsRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }

  async get(groupId: string, params?: { include: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new GetGroupRequest({
      token: accessToken,
      groupId,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async update(
    groupId: string,
    groupRequest: Group,
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const group = this.transform(groupRequest);
    const request = new UpdateGroupRequest({
      token: accessToken,
      groupId,
      group,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async create(group: Group) {
    const accessToken = this.userStore.accessToken;
    const request = new CreateGroupRequest({
      token: accessToken,
      group,
    });

    return this.apiClient.request(request);
  }

  async delete(groupId: string) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteGroupRequest({
      token: accessToken,
      groupId,
    });

    return this.apiClient.request(request);
  }

  transform(group: Group): GroupRequest {
    const {
      groupID,
      name,
      description,
      parent,
      stars,
      usersGroup,
      forms,
      features,
      training,
      documents,
      admins,
      emergencyContacts,
    } = group;
    let form_ids: Form['formID'][] = [];
    let feature_ids: Feature['featureID'][] = [];
    let training_ids: Training['id'][] = [];
    let document_ids: DocumentID[] = [];
    let admin_ids: User['userID'][] = [];

    forms?.data.forEach((form: Form) => {
      form_ids.push(form.formID);
    });

    features?.data.forEach((feature) => {
      feature_ids.push(feature.featureID);
    });

    training?.data.forEach((training) => {
      if (training.id) {
        training_ids.push(training.id);
      }
    });

    documents?.data.forEach((document) => {
      document_ids.push(document.documentID as DocumentID);
    });

    admins?.data.forEach((admin) => {
      admin_ids.push(admin.userID);
    });

    return {
      groupID,
      name,
      description,
      parent,
      stars,
      usersGroup,
      form_ids,
      feature_ids,
      training_ids,
      document_ids,
      admin_ids,
      contacts: emergencyContacts?.data || [],
    };
  }
}
